import React, { useState, useEffect, useCallback, useMemo } from 'react';

// Receive Activity — per-associate receiving (LP Receive / IB Problem Solve / Pallet
// Receive) for a selected date + shift, laid out like the IDRT tab's top table.
// Data: RECEIVE_ACTIVITY_<date>/<shift> (rollups, every sync pass) and
//       RECEIVE_SCANS_<date>/<shift> (activityDetails events, hourly) from
//       backend/receive_activity_sync.py; ONSITE_<site>_<date>/<shift> punches.
// Timeline colours: LP Receive / Pallet Receive / Other; Idle and Break/Lunch match IDRT.

export const COL = { L: '#8e44ad', P: '#ff8f00', O: '#00897b', idle: '#aed8f5', break: '#bdbdbd' };
export const CAT_LABEL = { L: 'LP Receive', P: 'Pallet Receive', O: 'Other' };
const PROC_LABEL = { LP: 'LP Rcv', PR: 'Pallet Rcv', PS: 'IB PS' };
// Receive-type filter: process key -> timeline event category it owns.
export const RTYPES = { all: 'All receives', LP: 'LP Receive', PR: 'Pallet Receive', PS: 'IB Problem Solve' };
const RTYPE_CAT = { LP: 'L', PR: 'P', PS: 'O' };

// Rows for a receive type + quarter (pure; unit-tested). 'all' = totals as synced.
export function filterRows(rawRows, rtype, quarter, shift) {
  const hrs = quarter === 'all' ? null : ((QUARTERS[shift] || QUARTERS.day)[quarter] || []);
  const out = [];
  for (const r of rawRows) {
    let h, j;
    if (!hrs) {
      if (rtype === 'all') { out.push(r); continue; }
      const b = (r.byProc || {})[rtype];
      if (!b || (!(b.h > 0) && !(b.j > 0))) continue;
      h = b.h || 0; j = b.j || 0;
    } else {
      h = 0; j = 0;
      hrs.forEach(H => {
        const b = (r.byHour || {})[H];
        if (!b) return;
        const v = rtype === 'all' ? b : (b.p || {})[rtype];
        if (v) { h += v.h || 0; j += v.j || 0; }
      });
      if (h <= 0 && j <= 0) continue;
    }
    h = Math.round(h * 100) / 100;
    out.push({ ...r, hours: h, jobs: j, cplh: h > 0 ? Math.round((j / h) * 10) / 10 : 0 });
  }
  return out;
}
const QUARTERS = {
  day: { q1: ['8', '9'], q2: ['10', '11', '12'], q3: ['13', '14', '15'], q4: ['16', '17', '18'] },
  night: { q1: ['19', '20'], q2: ['21', '22', '23'], q3: ['0', '1', '2'], q4: ['3', '4'] },
};
const QUARTER_LABELS = { q1: 'Q1 · SOS→Break 1', q2: 'Q2 · Break 1→Lunch', q3: 'Q3 · Lunch→Break 2', q4: 'Q4 · Break 2→EOS' };

// "HH:MM:SS<cat>" events -> [{ms, c}] on the shift's calendar (night hours < 12 are the next day).
export function eventsToMs(ev, date, shift) {
  const [Y, M, D] = date.split('-').map(Number);
  const list = typeof ev === 'string' ? (ev ? ev.split(',') : []) : (ev || []);   // stored as one comma-joined string
  return list.map(s => {
    const m = String(s).match(/^(\d{2}):(\d{2}):(\d{2})([LPO])$/);
    if (!m) return null;
    const h = +m[1];
    let ms = new Date(Y, M - 1, D, h, +m[2], +m[3]).getTime();
    if (shift === 'night' && h < 12) ms += 86400000;
    return { ms, c: m[4] };
  }).filter(Boolean).sort((a, b) => a.ms - b.ms);
}

// Same break-gap rule as IDRT: event-to-event gaps > 5 min, excused when inside a
// scheduled break/lunch window within ±5 min.
export function countBreakGaps(evs, shift) {
  const win = shift === 'day' ? [[600, 615], [960, 975], [765, 795]] : [[1260, 1275], [180, 195], [1425, 1440], [0, 15]];
  const mins = ms => { const d = new Date(ms); return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60; };
  const excused = (a, b, s, e) => !(b <= s || a >= e) && !(a < s - 5 || b > e + 5);
  let n = 0;
  for (let i = 1; i < evs.length; i++) {
    if (evs[i].ms - evs[i - 1].ms <= 5 * 60000) continue;
    const a = mins(evs[i - 1].ms), b = mins(evs[i].ms);
    if (!win.some(([s, e]) => excused(a, b, s, e))) n++;
  }
  return n;
}

// Timeline: minute steps SOS→end; break > activity (category of the nearest event
// within ±5 min, only between first and last event) > idle.
export function buildTimeline(evs, punches, date, shift, nowMs = Date.now()) {
  if (!evs.length) return { noScans: true };
  const [Y, M, D] = date.split('-').map(Number);
  const at = hhmm => { if (!hhmm || !/^\d{1,2}:\d{2}$/.test(hhmm)) return null; const [h, mi] = hhmm.split(':').map(Number); let t = new Date(Y, M - 1, D, h, mi).getTime(); if (shift === 'night' && h < 12) t += 86400000; return t; };
  const o = punches || {};
  const sos = at(o.sosPunch) || evs[0].ms;
  const endOut = at(o.endOut);
  const shiftEnd = shift === 'day' ? new Date(Y, M - 1, D, 18, 30).getTime() : new Date(Y, M - 1, D + 1, 5, 30).getTime();
  const end = endOut || Math.min(nowMs, shiftEnd);
  const bw = (h1, m1, h2, m2) => { let s = new Date(Y, M - 1, D, h1, m1).getTime(), e = new Date(Y, M - 1, D, h2, m2).getTime(); if (shift === 'night') { if (h1 < 12) s += 86400000; if (h2 < 12) e += 86400000; } return [s, e]; };
  const breaks = shift === 'day' ? [bw(10, 0, 10, 15), bw(16, 0, 16, 15), bw(12, 45, 13, 15)] : [bw(21, 0, 21, 15), bw(3, 0, 3, 15), bw(23, 45, 24, 15)];
  const lo = at(o.lunchOut), li = at(o.lunchIn);
  if (lo && li) breaks.push([lo, li]);
  const first = evs[0].ms, last = evs[evs.length - 1].ms, GAP = 5 * 60000;
  const segs = []; let cur = null, j = 0;
  const stop = Math.max(end, last + 60000);
  for (let t = sos; t < stop; t += 60000) {
    let type = 'idle';
    if (breaks.some(([s, e]) => t >= s && t < e)) type = 'break';
    else if (t >= first - 60000 && t <= last) {
      while (j < evs.length - 1 && evs[j + 1].ms <= t) j++;
      const cand = [evs[j], evs[j + 1]].filter(Boolean).filter(e => Math.abs(e.ms - t) <= GAP);
      if (cand.length) type = cand.sort((a, b) => Math.abs(a.ms - t) - Math.abs(b.ms - t))[0].c;
    }
    if (cur && cur.type === type) cur.endMs = t + 60000; else { cur = { startMs: t, endMs: t + 60000, type }; segs.push(cur); }
  }
  const markers = [];
  const mk = (ms, label) => { if (ms != null && ms >= sos && ms <= stop) markers.push({ ms, label }); };
  mk(sos, 'SOS'); mk(first, '1st scan'); mk(lo, 'Lunch out'); mk(li, 'Lunch in'); mk(last, 'Last scan'); if (endOut) mk(endOut, 'Clock out');
  const minsOf = k => segs.filter(s => s.type === k).reduce((a, s) => a + (s.endMs - s.startMs) / 60000, 0);
  return { sos, end: stop, segs, markers, count: evs.length, idleMin: minsOf('idle'), byCat: { L: minsOf('L'), P: minsOf('P'), O: minsOf('O') } };
}

export default function ReceiveActivity({ api, site }) {
  const [date, setDate] = useState('');
  const [shift, setShift] = useState('day');
  const [data, setData] = useState(null);
  const [scans, setScans] = useState(null);
  const [onsite, setOnsite] = useState({});
  const [loading, setLoading] = useState(false);
  const [quarter, setQuarter] = useState('all');
  const [rtype, setRtype] = useState('all');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const pst = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    const h = pst.getHours(), d = new Date(pst);
    if (h < 6) d.setDate(d.getDate() - 1);
    setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    setShift(h >= 8 && h < 19 ? 'day' : 'night');
  }, []);
  const get = useCallback(k => fetch(`${api}/api/fclm/cache?site=${site}&date=${k}&shift_type=${shift}`).then(r => r.json()).catch(() => null), [api, site, shift]);
  const load = useCallback(() => {
    if (!date) return;
    setLoading(true);
    Promise.all([get(`RECEIVE_ACTIVITY_${date}`), get(`RECEIVE_SCANS_${date}`), get(`ONSITE_${site}_${date}`)]).then(([a, s, o]) => {
      setData(a && Array.isArray(a.rows) ? a : null);
      setScans(s && s.byEmp ? s : null);
      const m = {}; ((o && o.onsite) || []).forEach(x => { if (x.empId) m[x.empId] = x; }); setOnsite(m);
    }).finally(() => setLoading(false));
  }, [get, date, site]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!date) return; const t = setInterval(load, 60000); return () => clearInterval(t); }, [load, date]);

  const rawRows = useMemo(() => data?.rows || [], [data]);
  // Quarter needs per-hour buckets; a type filter also needs the per-type split (byHour[HH].p).
  const hasByHour = rawRows.some(r => r && r.byHour && Object.values(r.byHour).some(b => rtype === 'all' || (b && b.p)));
  const quarterRows = useMemo(() => filterRows(rawRows, rtype, quarter, shift), [rawRows, rtype, quarter, shift]);
  const rows = [...quarterRows].sort((a, b) => (b.cplh || 0) - (a.cplh || 0));
  const worked = rows.filter(r => (r.hours || 0) > 0);
  const wmHours = worked.reduce((s, r) => s + (r.hours || 0), 0);
  const wmJobs = worked.reduce((s, r) => s + (r.jobs || 0), 0);
  const wmCplh = wmHours > 0 ? Math.round((wmJobs / wmHours) * 10) / 10 : 0;

  const evByEmp = useMemo(() => {
    const m = {};
    if (scans && date) Object.entries(scans.byEmp).forEach(([emp, v]) => {
      const evs = eventsToMs(v.ev, date, shift);
      m[emp] = rtype === 'all' ? evs : evs.filter(e => e.c === RTYPE_CAT[rtype]);
    });
    return m;
  }, [scans, date, shift, rtype]);
  const gaps = useMemo(() => Object.fromEntries(Object.entries(evByEmp).map(([k, v]) => [k, countBreakGaps(v, shift)])), [evByEmp, shift]);
  const hms = ms => new Date(ms).toLocaleTimeString('en-US', { timeZone: 'America/Los_Angeles', hour12: false });
  const first = emp => (rtype === 'all' ? scans?.byEmp?.[emp]?.first : (evByEmp[emp]?.[0] && hms(evByEmp[emp][0].ms))) || '';
  const last = emp => (rtype === 'all' ? scans?.byEmp?.[emp]?.last : (evByEmp[emp]?.length && hms(evByEmp[emp][evByEmp[emp].length - 1].ms))) || '';

  const cell = { border: '1px solid #ccc', padding: '1px 6px', fontSize: '0.68rem', lineHeight: 1.1 };
  const hdr = { ...cell, background: '#232f3e', color: '#fff', textAlign: 'left', fontWeight: 600 };
  const num = { ...cell, textAlign: 'right' };
  const totalStyle = { ...cell, fontWeight: 700, background: '#f1f8e9' };
  const totalNum = { ...num, fontWeight: 700, background: '#f1f8e9' };
  const mix = r => Object.entries(r.byProc || {}).filter(([, v]) => v.h > 0 || v.j > 0).map(([k, v]) => `${PROC_LABEL[k] || k} ${v.h.toFixed(1)}h`).join(' · ');
  const fmt = ms => new Date(ms).toLocaleTimeString('en-US', { timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit', hour12: false });
  const sw = c => <span style={{ display: 'inline-block', width: 10, height: 10, background: c, marginRight: 3 }} />;

  return (
    <div>
      <h2 className="section-title">Receive Activity</h2>
      <div className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <label style={{ fontSize: '0.75rem' }}>Date: <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ fontSize: '0.75rem' }} /></label>
        <label style={{ fontSize: '0.75rem' }}>Shift:{' '}
          <select value={shift} onChange={e => setShift(e.target.value)} style={{ fontSize: '0.75rem' }} aria-label="Shift"><option value="day">Day</option><option value="night">Night</option></select>
        </label>
        <label style={{ fontSize: '0.75rem' }}>Receive type:{' '}
          <select value={rtype} onChange={e => { setRtype(e.target.value); setExpanded(null); }} style={{ fontSize: '0.75rem' }} aria-label="Receive type">
            {Object.entries(RTYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        {data && (
          <span style={{ fontSize: '0.75rem', color: '#444' }}>
            <strong>{worked.length}</strong> associates · <strong>{Math.round(wmHours).toLocaleString()}</strong> hrs ·{' '}
            <strong>{wmJobs.toLocaleString()}</strong> jobs · CPLH <strong>{wmCplh}</strong>
            {rtype !== 'all' && <span style={{ color: '#1565c0', marginLeft: 6 }}>· {RTYPES[rtype]} only</span>}
            {quarter !== 'all' && <span style={{ color: '#1565c0', marginLeft: 6 }}>· {QUARTER_LABELS[quarter]}</span>}
          </span>
        )}
        {data?.fetched_at && <span style={{ fontSize: '0.65rem', color: '#888' }}>Updated {new Date(data.fetched_at).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · auto-refresh 60s</span>}
      </div>

      <div className="card" style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8, padding: '6px 10px' }}>
        <span style={{ fontSize: '0.7rem', color: '#555', marginRight: 2 }}>Shift quarter:</span>
        {[['all', 'All Day'], ['q1', 'Q1'], ['q2', 'Q2'], ['q3', 'Q3'], ['q4', 'Q4']].map(([q, lbl]) => (
          <button key={q} onClick={() => setQuarter(q)} disabled={q !== 'all' && !hasByHour}
            title={q !== 'all' && !hasByHour ? (rtype === 'all' ? 'Per-hour data not available yet (backend sync pending)' : 'Per-hour split by receive type not available yet for this shift (next sync pass)') : (QUARTER_LABELS[q] || 'Whole shift')}
            style={{ fontSize: '0.68rem', padding: '3px 10px', borderRadius: 3, border: '1px solid #ccc', cursor: (q !== 'all' && !hasByHour) ? 'not-allowed' : 'pointer', opacity: (q !== 'all' && !hasByHour) ? 0.45 : 1,
              background: quarter === q ? '#232f3e' : '#fff', color: quarter === q ? '#fff' : '#232f3e', fontWeight: quarter === q ? 700 : 400 }}>{lbl}</button>
        ))}
        {quarter !== 'all' && <span style={{ fontSize: '0.66rem', color: '#1565c0' }}>{QUARTER_LABELS[quarter]} · {worked.length} active AA{worked.length === 1 ? '' : 's'}</span>}
      </div>

      <div className="card" style={{ overflowX: 'auto' }}>
        {loading && !data ? <p style={{ fontSize: '0.75rem', color: '#888' }}>Loading…</p> : rows.length === 0 ? (
          <p style={{ fontSize: '0.8rem', color: '#888' }}>{rtype !== 'all' && rawRows.length ? `No associates did ${RTYPES[rtype]} in this ${quarter === 'all' ? 'shift' : 'quarter'}.` : `No receive activity cached for ${date || 'this date'} yet. Hours sync every pass (~25 min); scan detail hourly.`}</p>
        ) : (
          <table style={{ borderCollapse: 'collapse', width: 'auto' }}>
            <thead><tr>
              <th style={hdr}>Associate</th>
              {['Hours', 'Jobs', 'CPLH', 'First', 'Last', 'Breaks >5min'].map(h => <th key={h} style={{ ...hdr, textAlign: 'right' }}>{h}</th>)}
              <th style={hdr}>Process mix</th>
            </tr></thead>
            <tbody>
              <tr style={{ borderBottom: '2px solid #000' }}>
                <td style={totalStyle}>Total ({rows.length})</td>
                <td style={totalNum}>{rows.reduce((s, r) => s + (r.hours || 0), 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                <td style={totalNum}>{rows.reduce((s, r) => s + (r.jobs || 0), 0).toLocaleString()}</td>
                <td style={totalNum}>{(() => { const h = rows.reduce((s, r) => s + (r.hours || 0), 0), j = rows.reduce((s, r) => s + (r.jobs || 0), 0); return h > 0 ? Math.round((j / h) * 10) / 10 : 0; })()}</td>
                <td style={totalNum} /><td style={totalNum} />
                <td style={totalNum}>{quarter === 'all' ? rows.reduce((s, r) => s + (gaps[r.empId] || 0), 0) : ''}</td>
                <td style={totalNum} />
              </tr>
              <tr style={{ borderBottom: '2px solid #000' }}>
                <td style={totalStyle}>Total (without managers) ({worked.length})</td>
                <td style={totalNum}>{wmHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                <td style={totalNum}>{wmJobs.toLocaleString()}</td>
                <td style={totalNum}>{wmCplh}</td>
                <td style={totalNum} /><td style={totalNum} />
                <td style={totalNum}>{worked.reduce((s, r) => s + (gaps[r.empId] || 0), 0)}</td>
                <td style={totalNum} />
              </tr>
              {rows.map(r => {
                const open = expanded === r.empId;
                return (
                  <React.Fragment key={r.empId}>
                    <tr>
                      <td style={{ ...cell, cursor: 'pointer', userSelect: 'none' }} onClick={() => setExpanded(open ? null : r.empId)}>
                        <span style={{ color: '#888', marginRight: 4 }}>{open ? '▾' : '▸'}</span>{r.name}
                      </td>
                      <td style={num}>{r.hours.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                      <td style={num}>{r.jobs.toLocaleString()}</td>
                      <td style={{ ...num, fontWeight: 600 }}>{r.cplh}</td>
                      <td style={num}>{first(r.empId)}</td>
                      <td style={num}>{last(r.empId)}</td>
                      <td style={{ ...num, fontWeight: (gaps[r.empId] || 0) > 0 ? 600 : 400, color: (gaps[r.empId] || 0) >= 3 ? '#c0392b' : undefined }}>{quarter === 'all' ? (gaps[r.empId] || 0) : ''}</td>
                      <td style={{ ...cell, color: '#555' }}>{mix(r)}</td>
                    </tr>
                    {open && (() => {
                      const tl = buildTimeline(evByEmp[r.empId] || [], onsite[r.empId], date, shift);
                      return (
                        <tr><td colSpan={8} style={{ ...cell, background: '#fafafa', padding: '8px 10px' }}>
                          {tl.noScans ? <span style={{ fontSize: '0.7rem', color: '#f57f17' }}>No per-scan detail for this associate this shift yet (activity detail syncs hourly).</span> : (() => {
                            const span = tl.end - tl.sos || 1;
                            const pct = ms => `${Math.max(0, Math.min(100, (ms - tl.sos) / span * 100))}%`;
                            const wpct = (s, e) => `${Math.max(0, (e - s) / span * 100)}%`;
                            return (
                              <div>
                                <div style={{ display: 'flex', gap: 12, fontSize: '0.62rem', color: '#555', marginBottom: 4, flexWrap: 'wrap' }}>
                                  {rtype === 'all'
                                    ? <><span>{sw(COL.L)}LP Receive</span><span>{sw(COL.P)}Pallet Receive</span><span>{sw(COL.O)}Other</span></>
                                    : <span>{sw(COL[RTYPE_CAT[rtype]])}{rtype === 'PS' ? 'Other (incl. problem solve)' : RTYPES[rtype]} only</span>}
                                  <span>{sw(COL.idle)}Idle ≥5min</span><span>{sw(COL.break)}Break/Lunch</span>
                                  <span style={{ marginLeft: 'auto' }}>{tl.count} scans · LP {Math.round(tl.byCat.L)}m · Pallet {Math.round(tl.byCat.P)}m · Other {Math.round(tl.byCat.O)}m · {Math.round(tl.idleMin)} idle min · {fmt(tl.sos)}–{fmt(tl.end)}</span>
                                </div>
                                <div style={{ position: 'relative', height: 22, background: '#eee', borderRadius: 3, overflow: 'hidden' }}>
                                  {tl.segs.map((s, i) => <div key={i} title={`${CAT_LABEL[s.type] || (s.type === 'idle' ? 'Idle' : 'Break/Lunch')} ${fmt(s.startMs)}–${fmt(s.endMs)}`} style={{ position: 'absolute', left: pct(s.startMs), width: wpct(s.startMs, s.endMs), top: 0, bottom: 0, background: COL[s.type] }} />)}
                                </div>
                                <div style={{ position: 'relative', height: 26, marginTop: 1 }}>
                                  {tl.markers.map((m, i) => (
                                    <div key={i} style={{ position: 'absolute', left: pct(m.ms), transform: 'translateX(-50%)', textAlign: 'center' }}>
                                      <div style={{ width: 1, height: 5, background: '#555', margin: '0 auto' }} />
                                      <div style={{ fontSize: '0.54rem', color: '#555', whiteSpace: 'nowrap' }}>{m.label}<br />{fmt(m.ms)}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                        </td></tr>
                      );
                    })()}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
